import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { SignOut, Receipt, UserCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useCustomer } from "@/context/CustomerContext";
import { useI18n } from "@/context/I18nContext";

function errMsg(e, fallback) {
    const d = e?.response?.data?.detail;
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return d.map((x) => x.msg).join(", ");
    return fallback;
}

export default function AccountDialog({ open, onOpenChange }) {
    const { customer } = useCustomer();
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="account-dialog" className="sm:max-w-md max-h-[85vh] overflow-y-auto rounded-2xl">
                {customer ? <ProfileView onClose={() => onOpenChange(false)} /> : <AuthView />}
            </DialogContent>
        </Dialog>
    );
}

function AuthView() {
    const { lang } = useI18n();
    const es = lang === "es";
    const { login, register } = useCustomer();
    const [mode, setMode] = useState("login");
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", address: "" });
    const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const submit = async () => {
        if (!form.email || !form.password) {
            toast.error(es ? "Correo y contraseña son requeridos" : "Email and password are required");
            return;
        }
        if (mode === "register" && !form.name) {
            toast.error(es ? "El nombre es requerido" : "Name is required");
            return;
        }
        setBusy(true);
        try {
            if (mode === "login") {
                await login(form.email, form.password);
                toast.success(es ? "¡Bienvenido de nuevo!" : "Welcome back!");
            } else {
                await register(form);
                toast.success(es ? "¡Cuenta creada!" : "Account created!");
            }
        } catch (e) {
            toast.error(errMsg(e, es ? "Error de autenticación" : "Auth error"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div>
            <DialogHeader>
                <DialogTitle className="font-display text-2xl font-black tracking-tight">
                    {mode === "login"
                        ? es ? "Iniciar sesión" : "Sign in"
                        : es ? "Crear cuenta" : "Create account"}
                </DialogTitle>
                <DialogDescription className="sr-only">
                    {es ? "Accede o crea tu cuenta de cliente" : "Sign in or create your customer account"}
                </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 bg-muted rounded-full p-1 mt-3 mb-4">
                {["login", "register"].map((m) => (
                    <button
                        key={m}
                        data-testid={`account-tab-${m}`}
                        onClick={() => setMode(m)}
                        className={`py-2 rounded-full text-xs font-bold uppercase tracking-wider transition ${
                            mode === m ? "bg-foreground text-background" : "text-foreground/60"
                        }`}
                    >
                        {m === "login" ? (es ? "Entrar" : "Sign in") : (es ? "Registrarme" : "Register")}
                    </button>
                ))}
            </div>
            <div className="space-y-3">
                {mode === "register" && (
                    <Input name="name" placeholder={es ? "Nombre completo" : "Full name"} value={form.name} onChange={onChange} data-testid="account-name" />
                )}
                <Input name="email" type="email" placeholder={es ? "Correo electrónico" : "Email"} value={form.email} onChange={onChange} data-testid="account-email" />
                <Input
                    name="password"
                    type="password"
                    placeholder={es ? "Contraseña (mín. 6)" : "Password (min. 6)"}
                    value={form.password}
                    onChange={onChange}
                    data-testid="account-password"
                />
                {mode === "register" && (
                    <>
                        <Input name="phone" placeholder={es ? "Teléfono" : "Phone"} value={form.phone} onChange={onChange} data-testid="account-phone" />
                        <Input name="address" placeholder={es ? "Dirección" : "Address"} value={form.address} onChange={onChange} data-testid="account-address" />
                    </>
                )}
                <Button
                    data-testid="account-submit"
                    onClick={submit}
                    disabled={busy}
                    className="w-full py-6 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground font-bold uppercase tracking-widest"
                >
                    {busy ? "..." : mode === "login" ? (es ? "Entrar" : "Sign in") : (es ? "Crear cuenta" : "Create account")}
                </Button>
            </div>
        </div>
    );
}

function ProfileView({ onClose }) {
    const { lang } = useI18n();
    const es = lang === "es";
    const { customer, logout, updateProfile, fetchOrders } = useCustomer();
    const [form, setForm] = useState({
        name: customer.name || "",
        phone: customer.phone || "",
        address: customer.address || "",
    });
    const [orders, setOrders] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        fetchOrders().then(setOrders).catch(() => setOrders([]));
    }, [fetchOrders]);

    const save = async () => {
        if (!form.name) return toast.error(es ? "El nombre es requerido" : "Name is required");
        setBusy(true);
        try {
            await updateProfile(form);
            toast.success(es ? "Perfil actualizado" : "Profile updated");
        } catch (e) {
            toast.error(errMsg(e, es ? "No se pudo guardar" : "Could not save"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div>
            <DialogHeader>
                <DialogTitle className="font-display text-2xl font-black tracking-tight flex items-center gap-2">
                    <UserCircle size={26} weight="duotone" className="text-primary" />
                    {es ? "Mi cuenta" : "My account"}
                </DialogTitle>
                <DialogDescription className="sr-only">
                    {es ? "Perfil e historial de pedidos" : "Profile and order history"}
                </DialogDescription>
            </DialogHeader>
            <p className="text-sm text-muted-foreground mt-1" data-testid="profile-email">{customer.email}</p>

            <div className="space-y-3 mt-4">
                <Input name="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={es ? "Nombre completo" : "Full name"} data-testid="profile-name" />
                <Input name="phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={es ? "Teléfono" : "Phone"} data-testid="profile-phone" />
                <Input name="address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder={es ? "Dirección" : "Address"} data-testid="profile-address" />
                <Button
                    data-testid="profile-save"
                    onClick={save}
                    disabled={busy}
                    className="w-full rounded-full bg-foreground text-background font-bold uppercase tracking-widest text-xs"
                >
                    {busy ? "..." : es ? "Guardar cambios" : "Save changes"}
                </Button>
            </div>

            <div className="mt-6">
                <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-1.5 mb-2">
                    <Receipt size={14} weight="bold" />
                    {es ? "Mis pedidos" : "My orders"}
                </p>
                {orders === null ? (
                    <p className="text-sm text-muted-foreground py-4 text-center">...</p>
                ) : orders.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-4 text-center" data-testid="profile-no-orders">
                        {es ? "Todavía no tienes pedidos." : "No orders yet."}
                    </p>
                ) : (
                    <div className="space-y-2 max-h-52 overflow-y-auto" data-testid="profile-orders">
                        {orders.map((o) => (
                            <a
                                key={o.id}
                                href={`/order/${o.id}`}
                                className="flex items-center justify-between bg-muted/50 rounded-xl px-3 py-2.5 hover:bg-muted transition"
                                data-testid={`profile-order-${o.id}`}
                            >
                                <div className="min-w-0">
                                    <p className="text-sm font-bold truncate">
                                        {(o.items || []).map((i) => `${i.qty}× ${i.name}`).join(", ")}
                                    </p>
                                    <p className="text-[11px] text-muted-foreground">
                                        {o.created_at ? new Date(o.created_at).toLocaleDateString() : ""} · {o.status}
                                    </p>
                                </div>
                                <span className="font-display font-black text-sm shrink-0 ml-2">
                                    ${Number(o.total).toFixed(2)}
                                </span>
                            </a>
                        ))}
                    </div>
                )}
            </div>

            <button
                data-testid="profile-logout"
                onClick={() => { logout(); onClose(); }}
                className="mt-5 w-full flex items-center justify-center gap-2 py-2.5 rounded-full border border-border text-xs font-bold uppercase tracking-widest text-muted-foreground hover:text-destructive hover:border-destructive/40 transition"
            >
                <SignOut size={14} weight="bold" />
                {es ? "Cerrar sesión" : "Sign out"}
            </button>
        </div>
    );
}
